import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { mastersService } from '../utils/apiService';

const Agents = () => {
  const { currentTenant } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const data = await mastersService.getAllMasters(currentTenant.id);
      setAgents(data.agents || []);
    } catch (error) {
      setError(error.message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setFormData({
        name: agent.name || '',
        email: agent.email || '',
        phone: agent.phone || '',
        description: agent.description || ''
      });
    } else {
      setEditingAgent(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAgent(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      description: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgent) {
        await mastersService.updateMaster('agents', currentTenant.id, editingAgent._id, formData);
      } else {
        await mastersService.createMaster('agents', currentTenant.id, formData);
      }
      await fetchAgents();
      handleCloseDialog();
    } catch (error) {
      setError(error.message || 'Failed to save agent');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        await mastersService.deleteMaster('agents', currentTenant.id, id);
        await fetchAgents();
      } catch (error) {
        setError(error.message || 'Failed to delete agent');
      }
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Agents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Manage your agents
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => handleOpenDialog()}
          sx={{ 
            borderRadius: '4px',
            textTransform: 'none',
            py: 0.5,
            px: 1,
          }}
        >
          Add Agent
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 0.5, borderRadius: '4px' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                    No agents found
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => (
                  <TableRow key={agent._id} hover>
                    <TableCell sx={{ py: 0.75 }}>{agent.name}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{agent.email || '-'}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{agent.phone || '-'}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{agent.description || '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(agent)}
                        sx={{ p: 0.5, mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(agent._id)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: 400
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {editingAgent ? 'Edit Agent' : 'Add New Agent'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiInputBase-input': {
                    py: 0.75,
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiInputBase-input': {
                    py: 0.75,
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
              <TextField
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiInputBase-input': {
                    py: 0.75,
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                  },
                  '& .MuiInputBase-input': {
                    py: 0.75,
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            px: 2, 
            py: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: '#f7f7f7',
            gap: 1
          }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              size="small"
              sx={{ 
                borderRadius: '4px',
                textTransform: 'none',
                px: 1.5,
                py: 0.5,
                minWidth: '60px',
                fontSize: '0.8rem'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              size="small"
              sx={{ 
                borderRadius: '4px',
                textTransform: 'none',
                px: 1.5,
                py: 0.5,
                minWidth: '60px',
                fontSize: '0.8rem'
              }}
            >
              {editingAgent ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Agents; 